import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const API_BASE = (
  process.env.REACT_APP_API_URL || 'http://mmprfrontend.zoondia.org/api/v1'
).replace(/\/+$/, '');

/**
 * Opens a Server-Sent Events connection and streams new notifications in real
 * time.  For each incoming notification:
 *   - Displays a toast (error style for rejections, success style for all others)
 *   - Invalidates the React Query notification cache so the bell badge and
 *     notification list update immediately without a manual refresh
 *
 * The EventSource auto-reconnects on transient network failures.
 * The connection is closed when the component that calls this hook unmounts.
 *
 * @param {boolean} enabled  Pass false to skip opening the connection (e.g.
 *                           while the user is not yet authenticated).
 */
export function useNotificationStream(enabled = true) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const tokens = JSON.parse(localStorage.getItem('mmpr_tokens') || 'null');
    const token = tokens?.access;
    if (!token) return;

    const url = `${API_BASE}/notifications/stream/?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.onmessage = (event) => {
      try {
        const notif = JSON.parse(event.data);
        const isRejected = notif.notification_type === 'DOCUMENT_REJECTED';

        if (isRejected) {
          toast.error(`${notif.title}\n${notif.message}`, { duration: 7000 });
        } else {
          toast.success(`${notif.title}\n${notif.message}`, { duration: 6000 });
        }

        // Refresh the unread-count badge and notification list
        queryClient.invalidateQueries({ queryKey: ['notifications'] });

        // Re-fetch onboarding forms so the UI reflects the latest document
        // status (e.g. shows the rejection reason immediately)
        if (isRejected) {
          queryClient.invalidateQueries({ queryKey: ['onboarding-forms'] });
        }
      } catch {
        // Ignore malformed SSE data frames
      }
    };

    es.onerror = () => {
      // EventSource handles reconnection automatically; nothing to do here.
    };

    return () => {
      es.close();
    };
  }, [enabled, queryClient]);
}
